import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@shared/lib';
import { TokenIcon } from '@shared/ui';
import type { ChoicePrice, TokenLine } from '../lib/choicePrice';
import { formatMoscowTime } from '../lib/resetTime';
import type { SenseChoice } from '../lib/senseChoice';

/** Сколько будущих карточек перечислено в составе. */
const VISIBLE_CARDS = 3;

/**
 * Подпись кнопки создания с ценой (правило владельца «цена — на кнопке действия»):
 * «Создать 2 карточки · 1 жетон», «Создать карточку · 1 жетон»; всё уже готово — без цены;
 * жетоны закончились — «Жетоны закончились». `n` — сколько карточек создастся, не меньше 1.
 */
export function createButtonLabel(t: TFunction, n: number, price: ChoicePrice): string {
  if (price.blocked) return t('cardsWorkspace.choose.costExhausted');
  if (price.paid) {
    return n > 1 ? t('cardsWorkspace.choose.createPaid', { count: n }) : t('cardsWorkspace.choose.createOnePaid');
  }
  return n > 1 ? t('cardsWorkspace.choose.create', { count: n }) : t('cardsWorkspace.pick.createOne');
}

/**
 * Одна строка о жетонах справа в итоге: остаток после списания, «Без жетона · карточки уже
 * готовы» или когда придут новые. Цену второй раз не пишем — она на кнопке.
 */
export function ChoiceTokenLine({ line, className }: { line: TokenLine; className?: string }) {
  const { t } = useTranslation();
  if (line.kind === 'none') return null;
  if (line.kind === 'free') {
    return (
      <span className={cn('text-[14px] font-medium leading-[1.35] text-success', className)}>
        {t('cardsWorkspace.pick.priceNone')} · {t('cardsWorkspace.pick.priceNoneSub')}
      </span>
    );
  }
  const out = line.kind === 'out';
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[14px] leading-[1.35]', out ? 'text-warn' : 'text-muted', className)}>
      {/* Значок — по первой строке (середина строки 18,9 px), а не посередине двух-трёх строк переноса; в одну
          строку стоит там же, где по центру (приёмка A1-04). */}
      <TokenIcon size={14} className="mt-[2.5px] self-start" />
      <span>
        {line.kind === 'left'
          ? t('cardsWorkspace.choose.tokensLeft', { count: line.left, limit: line.limit })
          : line.kind === 'bonus'
            ? t('cardsWorkspace.choose.tokensBonus', { count: line.left })
            : t('cardsWorkspace.choose.quotaOut', { time: formatMoscowTime(line.resetAt) })}
      </span>
    </span>
  );
}

interface ChoiceSummaryProps {
  choice: SenseChoice;
  price: ChoicePrice;
  /** Ответ словаря на экране (иначе — ошибка поиска: без делений и подсказки «отметь»). */
  found: boolean;
  /** Справа после строки о жетонах — кнопка «Создать» (`WordSpotlight`). */
  action?: ReactNode;
  /** Под составом, когда жетоны закончились: на главной — «Добавить вручную», подарок, «Тарифы». */
  exhaustedActions?: ReactNode;
  /** Сразу за «Создастся 2 карточки»: на главной в компактной строке — «в «Путешествия»» (чип там — буква). */
  titleSuffix?: ReactNode;
  className?: string;
}

/**
 * Общий итог выбора значений (ТЗ главной § 6, § 15): «Создастся 2 карточки», 10 делений и
 * «3 из 10», ниже состав — до трёх карточек и «· ещё N»; справа одна строка о жетонах.
 * Один вид у `/cards/new` и у листа на главной.
 */
export function ChoiceSummary({ choice, price, found, action, exhaustedActions, titleSuffix, className }: ChoiceSummaryProps) {
  const { t } = useTranslation();
  const n = choice.cards.length;
  const shown = choice.cards.slice(0, VISIBLE_CARDS);
  return (
    <div className={cn('flex flex-wrap items-center gap-x-5 gap-y-3', className)}>
      <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-[5px]">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-[14px] font-semibold">
            {n
              ? t('cardsWorkspace.pick.willCreate', { count: n })
              : choice.allLocked
                ? t('cardsWorkspace.pick.allLearned')
                : t('cardsWorkspace.pick.nothing')}
            {n > 0 && titleSuffix}
          </span>
          {found && (
            <span className="flex items-center gap-2">
              <span className="flex gap-[3px]" aria-hidden>
                {Array.from({ length: choice.limit }, (_, i) => (
                  <span
                    key={i}
                    className="h-1 w-2 rounded-sm transition-colors duration-150"
                    style={{ background: i < choice.count ? (choice.atLimit ? '#ff9a5c' : '#f36a1c') : '#34343a' }}
                  />
                ))}
              </span>
              <span className={cn('text-[14px] font-medium', choice.atLimit ? 'text-[#ff9a5c]' : 'text-muted')}>
                {choice.atLimit
                  ? t('cardsWorkspace.pick.countMax', { limit: choice.limit })
                  : t('cardsWorkspace.pick.count', { n: choice.count, limit: choice.limit })}
              </span>
            </span>
          )}
        </div>
        <div className="line-clamp-2 text-[14px] leading-[1.45] text-[#c9c5c0]">
          {n > 0 ? (
            <>
              {shown.map((c, i) => (
                <span key={c.front}>
                  <b lang="en" className="font-serif text-[14px] font-semibold text-fg">
                    {c.front}
                  </b>
                  : {c.translations.join(', ')}
                  {i < shown.length - 1 ? ' · ' : ''}
                </span>
              ))}
              {n > VISIBLE_CARDS && (
                <span className="text-muted">{t('cardsWorkspace.pick.moreCards', { n: n - VISIBLE_CARDS })}</span>
              )}
            </>
          ) : (
            <span className="text-muted">{found ? t('cardsWorkspace.pick.pickAbove') : ''}</span>
          )}
        </div>
        {price.blocked && exhaustedActions}
      </div>
      <ChoiceTokenLine line={price.line} className="shrink-0 text-right" />
      {action}
    </div>
  );
}
